import type { PaginationQueryRequest, PaginationQueryResponse } from '@vsolv/dev-kit/nest';
import { Payment } from '@vsolv/packages/payments/domain';
import { Status } from './status';

/** The batch's status refined by its payment's status - refunds and failed charges show for what they are. */
export enum HistoryStatus {
  /** Charged successfully and its warranties funded. */
  PAID = 'PAID',

  /** Charge in flight — submitted, not yet settled or failed. */
  PROCESSING = 'PROCESSING',

  /** The last charge attempt failed; the batch stays locked for retry. */
  FAILED = 'FAILED',

  /** The payment was refunded, fully or partially. */
  REFUNDED = 'REFUNDED',

  DRAFT = 'DRAFT',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
}

export const HistoryStatusLabel: Record<HistoryStatus, string> = {
  [HistoryStatus.PAID]: 'Paid',
  [HistoryStatus.PROCESSING]: 'Processing',
  [HistoryStatus.FAILED]: 'Payment Failed',
  [HistoryStatus.REFUNDED]: 'Refunded',
  [HistoryStatus.DRAFT]: 'Draft',
  [HistoryStatus.CANCELLED]: 'Cancelled',
  [HistoryStatus.EXPIRED]: 'Expired',
};

/** Partial refunds keep HistoryStatus.REFUNDED but display with their own label. */
export const PartiallyRefundedLabel = 'Partially Refunded';

/** One row of a seller's payment history. Money is in cents; nulls mean the batch was never charged. */
export interface HistoryItem {
  id: string;

  status: HistoryStatus;
  batchStatus: Status;
  paymentStatus: Payment.Status | null;

  created: Date;
  /** When the charge actually settled; null until then. */
  transactionDate: Date | null;

  /** The gateway's transaction id. */
  referenceNumber: string | null;
  /** e.g. "Visa •••• 1234"; null until a payment method was used. */
  paymentMethod: string | null;

  /** payment.amount once charged, otherwise the sum of the batch's amountApplied snapshots. */
  totalAmount: number;
  refundedAmount: number | null;

  /** Download link for the payment's stored receipt PDF; null until one is generated. */
  receiptUrl: string | null;

  warrantyCount: number;
}

/** A warranty inside one batch, as the history detail view renders it. */
export interface HistoryWarranty {
  id: string;
  warrantyId: string;

  contractNumber: string;
  customerName: string | null;
  policyTitle: string | null;
  planTitle: string | null;

  /** Snapshot of the warranty's net amount due when it joined the batch, in cents. */
  amountApplied: number;
}

export interface ListHistoryRequest extends PaginationQueryRequest {
  status?: HistoryStatus[];
  startDate?: Date;
  endDate?: Date;
  /** Matches the payment's reference number, the batch id, or a contained warranty's contract number. */
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export type ListHistoryResponse = PaginationQueryResponse<HistoryItem>;
